import { formatDate, getRouteDisplayName, routePaletteColor } from './routeManagementHelpers';

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 500);
}

function timestamp() {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}`;
}

export async function exportStopsXlsx(stops, filenamePrefix = 'stops') {
  const xlsx = await import('xlsx');
  const rows = stops.map((stop, index) => ({
    '#': index + 1,
    'Stop ID': stop.stopId || '',
    Name: stop.stopName || '',
    Latitude: stop.lat,
    Longitude: stop.lng,
    Added: formatDate(stop.createdAt)
  }));
  const worksheet = xlsx.utils.json_to_sheet(rows);
  const workbook = xlsx.utils.book_new();
  xlsx.utils.book_append_sheet(workbook, worksheet, 'Stops');
  const buffer = xlsx.write(workbook, { bookType: 'xlsx', type: 'array' });
  downloadBlob(new Blob([buffer], { type: 'application/octet-stream' }), `${filenamePrefix}-${timestamp()}.xlsx`);
}

export async function exportRoutesXlsx(routes, resolveStopName, filenamePrefix = 'routes') {
  const xlsx = await import('xlsx');
  const rows = routes.map((route, index) => ({
    '#': index + 1,
    'Route name': getRouteDisplayName(route, resolveStopName),
    'Route no.': route.routeNumber || '',
    Direction: route.direction || '',
    'Stop count': route.stops?.length ?? 0,
    Added: formatDate(route.createdAt)
  }));
  const worksheet = xlsx.utils.json_to_sheet(rows);
  const workbook = xlsx.utils.book_new();
  xlsx.utils.book_append_sheet(workbook, worksheet, 'Routes');
  const buffer = xlsx.write(workbook, { bookType: 'xlsx', type: 'array' });
  downloadBlob(new Blob([buffer], { type: 'application/octet-stream' }), `${filenamePrefix}-${timestamp()}.xlsx`);
}

// jspdf-autotable v5 only patches `doc.autoTable` when jsPDF happens to be on
// `window` (script-tag usage), so under a bundler the table has to be drawn with
// the functional form: autoTable(doc, options).
async function loadJsPdf() {
  const [{ jsPDF }, { default: autoTable }] = await Promise.all([
    import('jspdf'),
    import('jspdf-autotable')
  ]);
  return { JsPDF: jsPDF, autoTable };
}

export async function exportStopsPdf(stops, filenamePrefix = 'stops') {
  const { JsPDF, autoTable } = await loadJsPdf();
  const doc = new JsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.text('Stops', 40, 40);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(100);
  doc.text(`${stops.length} record${stops.length === 1 ? '' : 's'} · exported ${new Date().toLocaleString()}`, 40, 58);

  autoTable(doc, {
    startY: 76,
    head: [['#', 'Stop ID', 'Name', 'Latitude', 'Longitude', 'Added']],
    body: stops.map((stop, index) => [
      index + 1,
      stop.stopId || '',
      stop.stopName || '',
      stop.lat?.toFixed(6) ?? '',
      stop.lng?.toFixed(6) ?? '',
      formatDate(stop.createdAt)
    ]),
    styles: { font: 'helvetica', fontSize: 9, cellPadding: 6 },
    headStyles: { fillColor: [241, 245, 249], textColor: [71, 85, 105], fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [250, 250, 250] }
  });

  doc.save(`${filenamePrefix}-${timestamp()}.pdf`);
}

export async function exportRoutesPdf(routes, resolveStopName, filenamePrefix = 'routes') {
  const { JsPDF, autoTable } = await loadJsPdf();
  const doc = new JsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.text('Routes', 40, 40);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(100);
  doc.text(`${routes.length} route${routes.length === 1 ? '' : 's'} · exported ${new Date().toLocaleString()}`, 40, 58);

  autoTable(doc, {
    startY: 76,
    head: [['#', 'Route name', 'No.', 'Direction', 'Stops', 'Added']],
    body: routes.map((route, index) => [
      index + 1,
      getRouteDisplayName(route, resolveStopName),
      route.routeNumber || '',
      route.direction || '',
      route.stops?.length ?? 0,
      formatDate(route.createdAt)
    ]),
    styles: { font: 'helvetica', fontSize: 9, cellPadding: 6 },
    headStyles: { fillColor: [241, 245, 249], textColor: [71, 85, 105], fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [250, 250, 250] }
  });

  doc.save(`${filenamePrefix}-${timestamp()}.pdf`);
}

// Build the .xlsx bytes for a single route (one sheet of its stops).
function buildRouteXlsxBytes(route, stopMap, resolveStopName, xlsx) {
  const routeName = getRouteDisplayName(route, resolveStopName);
  const rows = (route.stops || []).map((stopLink, stopIndex) => {
    const stop = stopMap.get(stopLink.stopId) || stopMap.get(String(stopLink.stopId));
    return {
      'Route name': routeName,
      'Route no.': route.routeNumber || '',
      Direction: route.direction || '',
      'Stop #': stopIndex + 1,
      'Stop ID': stop?.stopId || stopLink.stopId || '',
      'Stop name': stop?.stopName || stopLink.stopName || '',
      Latitude: stop?.lat ?? stopLink.lat ?? '',
      Longitude: stop?.lng ?? stopLink.lng ?? ''
    };
  });
  const worksheet = xlsx.utils.json_to_sheet(rows);
  const workbook = xlsx.utils.book_new();
  xlsx.utils.book_append_sheet(workbook, worksheet, 'Route');
  const out = xlsx.write(workbook, { bookType: 'xlsx', type: 'array' });
  return out instanceof Uint8Array ? out : new Uint8Array(out);
}

// One .xlsx per route, named "RouteNo.RouteName". A single route downloads as a
// lone .xlsx; multiple routes download as a .zip of those separate files.
export async function exportSelectedRoutesXlsx(routes, stops, resolveStopName, filenamePrefix = 'routes') {
  const xlsx = await import('xlsx');
  const stopMap = new Map(stops.map((s) => [s._id, s]));

  if (routes.length === 1) {
    const bytes = buildRouteXlsxBytes(routes[0], stopMap, resolveStopName, xlsx);
    downloadBlob(new Blob([bytes], { type: 'application/octet-stream' }), `${routeFileBaseName(routes[0], resolveStopName)}.xlsx`);
    return;
  }

  const JSZip = (await import('jszip')).default;
  const zip = new JSZip();
  const used = new Set();
  routes.forEach((route) => {
    const name = uniqueName(routeFileBaseName(route, resolveStopName), 'xlsx', used);
    zip.file(name, buildRouteXlsxBytes(route, stopMap, resolveStopName, xlsx));
  });
  const blob = await zip.generateAsync({ type: 'blob' });
  downloadBlob(blob, `${filenamePrefix}-${timestamp()}.zip`);
}

function hexToRgb(hex) {
  const value = String(hex).replace('#', '');
  return [
    parseInt(value.slice(0, 2), 16),
    parseInt(value.slice(2, 4), 16),
    parseInt(value.slice(4, 6), 16)
  ];
}

// One .pdf per route, named "RouteNo.RouteName": a branded stop-schedule
// table plus a map of the plotted stops. A single route downloads as a lone
// .pdf; multiple routes download as a .zip of those separate documents.
//
// `onProgress(done, total)` fires before each document so the caller can show
// progress — a large selection takes a while because every map is rasterised.
export async function exportSelectedRoutesPdf(
  routes,
  stops,
  resolveStopName,
  filenamePrefix = 'routes',
  onProgress
) {
  if (!routes.length) return;

  const { buildRoutePdfBlob, loadLogoDataUrl } = await import('./routePdf');
  const logoDataUrl = await loadLogoDataUrl();
  const stopMasterById = new Map(stops.map((stop) => [String(stop._id), stop]));

  const buildFor = (route, index) => {
    const hex = routePaletteColor(index);
    return buildRoutePdfBlob({
      route,
      stopMasterById,
      resolveStopName,
      accentColor: { hex, rgb: hexToRgb(hex) },
      logoDataUrl
    });
  };

  if (routes.length === 1) {
    onProgress?.(0, 1);
    const blob = await buildFor(routes[0], 0);
    onProgress?.(1, 1);
    downloadBlob(blob, `${routeFileBaseName(routes[0], resolveStopName)}.pdf`);
    return;
  }

  const JSZip = (await import('jszip')).default;
  const zip = new JSZip();
  const used = new Set();

  // Sequential on purpose: each document rasterises its own map, so building
  // them in parallel would spike memory and hammer the tile server.
  for (let index = 0; index < routes.length; index += 1) {
    onProgress?.(index, routes.length);
    // eslint-disable-next-line no-await-in-loop
    const blob = await buildFor(routes[index], index);
    zip.file(uniqueName(routeFileBaseName(routes[index], resolveStopName), 'pdf', used), blob);
  }
  onProgress?.(routes.length, routes.length);

  const archive = await zip.generateAsync({ type: 'blob' });
  downloadBlob(archive, `${filenamePrefix}-${timestamp()}.zip`);
}

// Build the full KML document string for one or more routes (one <Folder> each).
function buildKmlForRoutes(routes, stopMap, resolveStopName) {
  // KML header and shared styles (icon + stylemap)
  let kmlContent = '<?xml version="1.0" encoding="UTF-8"?>\n';
  kmlContent += '<kml xmlns="http://www.opengis.net/kml/2.2">\n';
  kmlContent += '  <Document>\n';

  // Use a single style id for all placemarks (mirrors reference file)
  const styleId = 'icon-1899-0288D1';
  kmlContent += `    <Style id="${styleId}-normal">\n`;
  kmlContent += '      <IconStyle>\n';
  kmlContent += '        <color>ffd18802</color>\n';
  kmlContent += '        <scale>1</scale>\n';
  kmlContent += '        <Icon>\n';
  kmlContent += '          <href>https://www.gstatic.com/mapspro/images/stock/503-wht-blank_maps.png</href>\n';
  kmlContent += '        </Icon>\n';
  kmlContent += '        <hotSpot x="32" xunits="pixels" y="64" yunits="insetPixels"/>\n';
  kmlContent += '      </IconStyle>\n';
  kmlContent += '      <LabelStyle>\n';
  kmlContent += '        <scale>0</scale>\n';
  kmlContent += '      </LabelStyle>\n';
  kmlContent += '    </Style>\n';

  kmlContent += `    <Style id="${styleId}-highlight">\n`;
  kmlContent += '      <IconStyle>\n';
  kmlContent += '        <color>ffd18802</color>\n';
  kmlContent += '        <scale>1</scale>\n';
  kmlContent += '        <Icon>\n';
  kmlContent += '          <href>https://www.gstatic.com/mapspro/images/stock/503-wht-blank_maps.png</href>\n';
  kmlContent += '        </Icon>\n';
  kmlContent += '        <hotSpot x="32" xunits="pixels" y="64" yunits="insetPixels"/>\n';
  kmlContent += '      </IconStyle>\n';
  kmlContent += '      <LabelStyle>\n';
  kmlContent += '        <scale>1</scale>\n';
  kmlContent += '      </LabelStyle>\n';
  kmlContent += '    </Style>\n';

  kmlContent += `    <StyleMap id="${styleId}">\n`;
  kmlContent += '      <Pair>\n';
  kmlContent += '        <key>normal</key>\n';
  kmlContent += `        <styleUrl>#${styleId}-normal</styleUrl>\n`;
  kmlContent += '      </Pair>\n';
  kmlContent += '      <Pair>\n';
  kmlContent += '        <key>highlight</key>\n';
  kmlContent += `        <styleUrl>#${styleId}-highlight</styleUrl>\n`;
  kmlContent += '      </Pair>\n';
  kmlContent += '    </StyleMap>\n';

  // Helper to format boolean direction to TRUE/FALSE as in reference
  const directionBool = (dir) => {
    if (!dir) return '';
    const d = String(dir).toLowerCase();
    if (d === 'up' || d === 'true') return 'TRUE';
    if (d === 'down' || d === 'false') return 'FALSE';
    return '';
  };

  routes.forEach((route) => {
    const routeName = getRouteDisplayName(route, resolveStopName);
    const routeNumber = route.routeNumber || route.routeId || routeName || '';
    const direction = route.direction || '';
    const routeStops = route.stops || [];

    const folderName = `${routeNumber} ${routeName}`.trim();

    kmlContent += `    <Folder>\n`;
    kmlContent += `      <name>${escapeXml(folderName)}</name>\n`;

    routeStops.forEach((stopLink, index) => {
      const stop = stopMap.get(stopLink.stopId) || stopMap.get(String(stopLink.stopId));

      // Build fields in the explicit order required by the target schema
      const fields = {
        route_number: routeNumber,
        stop_name_mar: stop?.stopNameMar || stopLink.stop_name_mar || '',
        stage_order_number: stopLink.stage_order_number || stopLink.sequence_number || index + 1,
        fare_stage_number: stopLink.fare_stage_number || '',
        is_state_change: stopLink.is_state_change || '',
        route_direction: directionBool(direction),
        route_name: routeName,
        WaypointNo: stopLink.WaypointNo || stopLink.waypointNo || '',
        WpointName: stop?.stopName || stopLink.stopName || stopLink.WpointName || '',
        Lat: stop?.lat ?? stopLink.lat ?? '',
        Long: stop?.lng ?? stopLink.lng ?? stopLink.Long ?? '',
        RouteNumberETA: stopLink.RouteNumberETA || '',
        RouteDirectionETA: stopLink.RouteDirectionETA || '',
        waypointseqNo: stopLink.waypointseqNo || stopLink.stage_order_number || stopLink.sequence_number || index + 1,
        stopcode: stopLink.stopcode || stopLink.stopCode || '',
        stopID: stop?.stopId || stopLink.stopId || stopLink.stop_id || '',
        MrouteId: stopLink.MrouteId || '',
        route_name_Eng: route.routeNameEng || route.route_name_Eng || ''
      };

      kmlContent += `      <Placemark>\n`;
      // Placemark name should be the stop name itself
      kmlContent += `        <name>${escapeXml(fields.WpointName || '')}</name>\n`;

      // description as CDATA with <br> separators (fields order preserved)
      const descLines = [];
      Object.entries(fields).forEach(([k, v]) => {
        if (v !== undefined && v !== null && String(v) !== '') descLines.push(`${k}: ${escapeXml(String(v))}`);
      });
      const descHtml = descLines.join('<br>');
      kmlContent += `        <description><![CDATA[${descHtml}]]></description>\n`;

      // styleUrl
      kmlContent += `        <styleUrl>#${styleId}</styleUrl>\n`;

      // ExtendedData with structured Data elements (ordered)
      kmlContent += '        <ExtendedData>\n';
      Object.entries(fields).forEach(([k, v]) => {
        const value = v === undefined || v === null ? '' : String(v);
        kmlContent += `          <Data name="${escapeXml(k)}">\n`;
        kmlContent += `            <value>${escapeXml(value)}</value>\n`;
        kmlContent += '          </Data>\n';
      });
      kmlContent += '        </ExtendedData>\n';

      // Point
      const lon = fields.Long || '';
      const lat = fields.Lat || '';
      kmlContent += '        <Point>\n';
      kmlContent += '          <coordinates>\n';
      kmlContent += `            ${lon},${lat},0\n`;
      kmlContent += '          </coordinates>\n';
      kmlContent += '        </Point>\n';

      kmlContent += '      </Placemark>\n';
    });

    kmlContent += '    </Folder>\n';
  });

  kmlContent += '  </Document>\n';
  kmlContent += '</kml>\n';
  return kmlContent;
}

// One .kml per route, named "RouteNo.RouteName". A single route downloads as a
// lone .kml; multiple routes download as a .zip of those separate files.
export async function exportSelectedRoutesKml(routes, stops, resolveStopName, filenamePrefix = 'routes') {
  const stopMap = new Map(stops.map((s) => [s._id, s]));

  if (routes.length === 1) {
    const kml = buildKmlForRoutes([routes[0]], stopMap, resolveStopName);
    downloadBlob(
      new Blob([kml], { type: 'application/vnd.google-earth.kml+xml' }),
      `${routeFileBaseName(routes[0], resolveStopName)}.kml`
    );
    return;
  }

  const JSZip = (await import('jszip')).default;
  const zip = new JSZip();
  const used = new Set();
  routes.forEach((route) => {
    const name = uniqueName(routeFileBaseName(route, resolveStopName), 'kml', used);
    zip.file(name, buildKmlForRoutes([route], stopMap, resolveStopName));
  });
  const blob = await zip.generateAsync({ type: 'blob' });
  downloadBlob(blob, `${filenamePrefix}-${timestamp()}.zip`);
}

function escapeXml(unsafe) {
  if (!unsafe) return '';
  return String(unsafe)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

// Windows reserved device names that can never be used as a filename.
const WINDOWS_RESERVED = /^(con|prn|aux|nul|com[1-9]|lpt[1-9])$/i;

// Produce a name that Windows Explorer (the strictest common extractor) will
// accept. Windows refuses to create files with control chars, the punctuation
// below, a trailing dot/space, a reserved device name, or an over-long path —
// any one of those makes "Extract All" fail with 0x80004005 and abort the rest
// of the batch. We strip all of them here.
function sanitizeFilename(name) {
  let cleaned = String(name)
    // eslint-disable-next-line no-control-regex
    .replace(/[\x00-\x1f\x7f]/g, '')   // control characters
    .replace(/[\\/:*?"<>|]/g, '-')     // Windows-illegal punctuation
    .replace(/\s+/g, ' ')              // collapse runs of whitespace
    .trim()
    .replace(/[. ]+$/, '');            // no trailing dot or space (Windows strips these)
  // Keep names short enough that the extracted path stays under MAX_PATH (260).
  if (cleaned.length > 120) cleaned = cleaned.slice(0, 120).trim().replace(/[. ]+$/, '');
  if (WINDOWS_RESERVED.test(cleaned)) cleaned = `_${cleaned}`;
  return cleaned;
}

// The download base name for a route: "RouteNo.RouteName" (e.g. "22.Thane
// Station - Modern Company"). Used for both single and multi (zip) downloads.
function routeFileBaseName(route, resolveStopName) {
  const routeNumber = route.routeNumber || route.routeId || '';
  const routeName = getRouteDisplayName(route, resolveStopName) || route.routeName || '';
  const raw = routeNumber && routeName
    ? `${routeNumber}.${routeName}`
    : routeNumber || routeName;
  return sanitizeFilename(raw) || 'route';
}

// Ensure a unique "<base>.<ext>" within one export batch so two routes that map
// to the same name don't overwrite each other inside the zip.
function uniqueName(base, ext, used) {
  let name = `${base}.${ext}`;
  let i = 2;
  while (used.has(name)) {
    name = `${base} (${i})`.concat('.', ext);
    i += 1;
  }
  used.add(name);
  return name;
}
